import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Excel Export Functions
export const exportToExcel = (data, filename, sheetName = 'Sheet1') => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// PDF Export Functions
export const exportToPDF = (columns, data, filename, title) => {
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(18);
  doc.text(title, 14, 20);
  
  // Add date
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);
  
  // Add table using autoTable
  autoTable(doc, {
    startY: 35,
    head: [columns],
    body: data,
    theme: 'grid',
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
    headStyles: {
      fillColor: [15, 23, 42], // slate-900
      textColor: 255,
      fontStyle: 'bold'
    }
  });
  
  doc.save(`${filename}.pdf`);
};

// Submittals Export
export const exportSubmittals = (submittals, format = 'excel') => {
  const data = submittals.map(s => ({
    'Title': s.title,
    'Description': s.description || '',
    'Raised By': s.assigned_by_name || 'Unknown',
    'Primary Reviewer': s.assigned_to_name || 'Unknown',
    'Reviewer 2': s.assigned_to_2_name || '-',
    'Reviewer 3': s.assigned_to_3_name || '-',
    'Workflow': s.workflow_type || 'simultaneous',
    'Priority': s.priority,
    'Due Date': new Date(s.due_date).toLocaleDateString(),
    'Issued Date': new Date(s.issued_date).toLocaleDateString(),
    'Status': s.status,
    'Approval 1': s.approval_1 || '-',
    'Approval 2': s.approval_2 || '-',
    'Approval 3': s.approval_3 || '-',
    'File': s.file_url ? 'Yes' : 'No',
    'Review Comments': s.review_comments || ''
  }));
  
  if (format === 'excel') {
    exportToExcel(data, 'Submittals', 'Submittals');
  } else {
    const columns = ['Title', 'Raised By', 'Reviewers', 'Workflow', 'Priority', 'Due Date', 'Status'];
    const rows = submittals.map(s => [
      s.title,
      s.assigned_by_name || 'Unknown',
      [s.assigned_to_name, s.assigned_to_2_name, s.assigned_to_3_name].filter(Boolean).join(', '),
      s.workflow_type || 'simultaneous',
      s.priority,
      new Date(s.due_date).toLocaleDateString(),
      s.status
    ]);
    exportToPDF(columns, rows, 'Submittals', 'Submittals Report');
  }
};

// RFIs Export
export const exportRFIs = (rfis, format = 'excel') => {
  const data = rfis.map((r, index) => ({
    'RFI #': `#${String(index + 1).padStart(3, '0')}`,
    'Title': r.title,
    'Description': r.description,
    'Raised By': r.raised_by_name || 'Unknown',
    'Assigned To': r.assigned_to_name || 'Unknown',
    'Response By': r.response_by_name || '-',
    'Spec Section': r.specification_section || '',
    'Drawing Ref': r.drawing_reference || '',
    'Priority': r.priority,
    'Status': r.status,
    'Due Date': new Date(r.due_date).toLocaleDateString(),
    'Cost Impact': r.cost_impact || 'none',
    'Schedule Impact': r.schedule_impact || 'none',
    'Response': r.response || 'Pending'
  }));
  
  if (format === 'excel') {
    exportToExcel(data, 'RFIs', 'RFIs');
  } else {
    const columns = ['RFI #', 'Title', 'Raised By', 'Assigned To', 'Response By', 'Priority', 'Status', 'Due Date'];
    const rows = data.map(d => [d['RFI #'], d.Title, d['Raised By'], d['Assigned To'], d['Response By'], d.Priority, d.Status, d['Due Date']]);
    exportToPDF(columns, rows, 'RFIs', 'RFIs Report');
  }
};

// Budget Export
export const exportBudget = (budgets, format = 'excel') => {
  const data = budgets.map(b => {
    const revisedBudget = b.budget_amount + b.approved_change_orders;
    const variance = revisedBudget - b.actual_spend;
    return {
      'Category': b.category || 'N/A',
      'Contractor': b.contractor_name,
      'Original Budget': b.budget_amount,
      'Change Orders': b.approved_change_orders,
      'Revised Budget': revisedBudget,
      'Actual Spend': b.actual_spend,
      'Variance': variance,
      'Comments': b.comments || ''
    };
  });
  
  // Add totals row
  const totals = {
    'Category': 'TOTALS',
    'Contractor': '',
    'Original Budget': data.reduce((sum, d) => sum + d['Original Budget'], 0),
    'Change Orders': data.reduce((sum, d) => sum + d['Change Orders'], 0),
    'Revised Budget': data.reduce((sum, d) => sum + d['Revised Budget'], 0),
    'Actual Spend': data.reduce((sum, d) => sum + d['Actual Spend'], 0),
    'Variance': data.reduce((sum, d) => sum + d['Variance'], 0),
    'Comments': ''
  };
  data.push(totals);
  
  if (format === 'excel') {
    exportToExcel(data, 'Budget', 'Budget');
  } else {
    const columns = ['Category', 'Contractor', 'Original Budget', 'Change Orders', 'Revised Budget', 'Actual Spend', 'Variance'];
    const rows = data.map(d => [
      d.Category, 
      d.Contractor, 
      `$${d['Original Budget'].toLocaleString()}`,
      `$${d['Change Orders'].toLocaleString()}`,
      `$${d['Revised Budget'].toLocaleString()}`,
      `$${d['Actual Spend'].toLocaleString()}`,
      `$${d.Variance.toLocaleString()}`
    ]);
    exportToPDF(columns, rows, 'Budget', 'Project Budget Report');
  }
};

// Change Orders Export
export const exportChangeOrders = (changeOrders, format = 'excel') => {
  const data = changeOrders.map((co, index) => ({
    'CO #': `CO-${String(index + 1).padStart(3, '0')}`,
    'Title': co.title,
    'Description': co.description,
    'Contractor': co.contractor_name,
    'Amount': co.amount,
    'Status': co.status,
    'Created': new Date(co.created_at).toLocaleDateString(),
    'File': co.file_url ? 'Yes' : 'No'
  }));
  
  if (format === 'excel') {
    exportToExcel(data, 'Change_Orders', 'Change Orders');
  } else {
    const columns = ['CO #', 'Title', 'Contractor', 'Amount', 'Status', 'Created'];
    const rows = data.map(d => [d['CO #'], d.Title, d.Contractor, `$${d.Amount.toLocaleString()}`, d.Status, d.Created]);
    exportToPDF(columns, rows, 'Change_Orders', 'Change Orders Report');
  }
};

// Meeting Minutes Export
export const exportMeetingMinutes = (minutes, format = 'excel') => {
  const data = minutes.map(m => ({
    'Title': m.title,
    'Meeting Date': new Date(m.meeting_date).toLocaleDateString(),
    'Attendees': m.attendees.join(', '),
    'Agenda': m.agenda,
    'Notes': m.notes,
    'Action Items': m.action_items || '',
    'Created': new Date(m.created_at).toLocaleDateString()
  }));
  
  if (format === 'excel') {
    exportToExcel(data, 'Meeting_Minutes', 'Meeting Minutes');
  } else {
    // For meeting minutes, create a more detailed PDF
    const doc = new jsPDF();
    let yPosition = 20;
    
    doc.setFontSize(18);
    doc.text('Meeting Minutes Report', 14, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, yPosition);
    yPosition += 10;
    
    minutes.forEach((minute, index) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`${index + 1}. ${minute.title}`, 14, yPosition);
      yPosition += 8;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Date: ${new Date(minute.meeting_date).toLocaleDateString()}`, 14, yPosition);
      yPosition += 6;
      
      // Wrap long attendees text
      const attendeesText = `Attendees: ${minute.attendees.join(', ')}`;
      const splitAttendees = doc.splitTextToSize(attendeesText, 180);
      doc.text(splitAttendees, 14, yPosition);
      yPosition += splitAttendees.length * 5 + 2;
      
      if (minute.agenda) {
        const agendaText = `Agenda: ${minute.agenda}`;
        const splitAgenda = doc.splitTextToSize(agendaText, 180);
        doc.text(splitAgenda, 14, yPosition);
        yPosition += splitAgenda.length * 5 + 2;
      }
      
      yPosition += 8;
    });
    
    doc.save('Meeting_Minutes.pdf');
  }
};
